<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { ref, computed, watch } from 'vue';
import { $fetch } from '@@home/plugins/fetch';
import { watchDebounced } from '@vueuse/core';

// 每页数量
const limit = 15;

const searchEl = ref(null);
const isOpen = ref(false);
// 主分类ID
const mainValue = ref('');
const search = ref('');
// 数据的当前页数
const currentPage = ref(1);
// 用户输入的当前页数
const current = ref(1);
const categories = ref<Record<string, any>[]>([]);
const contributorListing = ref<Record<string, any>[]>([]);
const contributorTotal = ref(0);
const contributors = ref<Record<string, any>[]>([]);

const refresh = async () => {
  getContributors();
  getLogos();
}

const getContributors = async () => {
  const { isSuccess, data } = await $fetch('/donor/v1/list', {
    data: {
      mc: mainValue.value,
      donorName: search.value,
      offset: (currentPage.value - 1) * limit,
      limit,
    }
  });

  if (isSuccess) {
    contributorListing.value = data.donorList ?? [];
    contributorTotal.value = data.totalCount;
  }
}

const getLogos = async () => {
  const { isSuccess, data } = await $fetch('/donor/v1/category', {
    data: {
      mc: mainValue.value,
      donorName: search.value,
    }
  });

  if (isSuccess) {
    contributors.value = data?.[0]?.subCategory ?? [];
  }
}

const getCategories = async () => {
  const { isSuccess, data } = await $fetch('/donor/v1/category/selector');

  if (isSuccess) {
    categories.value = data;
    mainValue.value = data?.[0]?.categoryId ?? '';
  }
}

const init = async () => {
  await getCategories();
  await refresh();
}

init();

const totalPage = computed(() => {
  return Math.ceil(contributorTotal.value / limit);
});

const previous = () => {
  if (currentPage.value < 2) {
    return;
  }

  currentPage.value--;
  current.value = currentPage.value;

  refresh();
}

const next = () => {
  if (currentPage.value >= totalPage.value) {
    return;
  }

  currentPage.value++;
  current.value = currentPage.value;

  refresh();
}

// 提交页码
const submit = () => {
  if (isNaN(Number(current.value))) {
    current.value = 1;
  }

  if (current.value < 1 || current.value > totalPage.value) {
    return;
  }

  currentPage.value = current.value;

  refresh();
}

// 提交搜索
const submitSearch = () => {
  currentPage.value = 1;
  current.value = 1;

  refresh();
}

const blur = () => {
  current.value = currentPage.value;
}

onClickOutside(searchEl, () => {
  isOpen.value = false;
});

watch(mainValue, () => {
  currentPage.value = 1;
  current.value = 1;

  refresh();
});

watchDebounced(
  search,
  () => {
    submitSearch();
  },  {
    debounce: 500
  },
)
</script>

<template>
  <section class="page-cooperation-partner">
    <div class="cooperation-partner-container mx-auto">
      <h3 class="m-0 text-center font-medium">{{ $t('cooperation.partner.title') }}</h3>

      <div class="cooperation-partner-content bg-contain bg-top bg-no-repeat">
        <div class="partner-content-box mx-auto">
          <div class="partner-content-bd__toolbar">
            <div class="search relative" :class="{ active: isOpen }" ref="searchEl">
              <div class="input-group relative flex">
                <div class="icon flex items-center justify-center">
                  <span class="i-home-icons:search"></span>
                </div>
                <input type="text" class="block flex-grow border-0 focus:outline-none" :placeholder="$t('cooperation.partner.searchInputPlaceholder')" @focus="isOpen = true" v-model="search">
              </div>
              <!-- <div class="popover absolute left-0 right-0" v-show="isOpen">
                <dl class="m-0">
                  <dt class="m-0">{{ $t('cooperation.partner.searchInputTitle1') }}</dt>
                  <dd class="m-0">
                    <ul class="m-0 flex list-none p-0">
                      <li>白金捐赠人</li>
                      <li>白金捐赠人</li>
                    </ul>
                  </dd>
                  <dt class="m-0">{{ $t('cooperation.partner.searchInputTitle2') }}</dt>
                  <dd class="m-0">
                    <ul class="m-0 flex list-none p-0">
                      <li>白金捐赠人</li>
                      <li>白金捐赠人</li>
                    </ul>
                  </dd>
                </dl>
              </div> -->
            </div>

            <div class="filters mt-48px pb-8px">
              <ul class="category-main m-0 flex flex-wrap list-none items-end gap-x-32px gap-y-24px p-0">
                <li v-for="item in categories" :key="item.categoryId">
                  <label>
                    <input type="radio" name="main" :value="item.categoryId" v-model="mainValue">
                    <span class="label-text">{{ item.categoryName }}</span>
                  </label>
                </li>
              </ul>
            </div>
          </div>

          <div class="partner-content-bd__table min-h-825px">
            <div v-if="contributors">
              <template v-for="contributor in contributors" :key="contributor.categoryId">
                <div class="contributors mt-48px first:mt-0 last:mb-48px" v-if="contributor?.donorList?.length">
                  <h4 class="relative m-0 flex items-center justify-between text-center font-normal leading-48px after:block after:w-full after:content-empty before:block before:w-full before:content-empty">
                    <span class="relative z-1 inline-block flex-shrink-0">{{ contributor.categoryName }}</span>
                  </h4>

                  <ul class="platinum-contributors m-0 flex flex-shrink flex-wrap list-none gap-x-42px gap-y-16px p-0 <2xl:gap-x-2px <2xl:gap-y-0" :class="{ 'justify-center': contributor.donorList.length < 3 }">
                    <template v-for="item in contributor.donorList" :key="item.donorId">
                      <li v-if="item.logo">
                        <img :data-src="item.logo" class="lazyload" :alt="item.donorName" :title="item.donorName">
                      </li>
                    </template>
                  </ul>
                </div>
              </template>
            </div>

            <div>
              <h4 class="m-0 text-center text-24px font-medium leading-48px">{{ $t('cooperation.partner.information.title') }}</h4>

              <div class="mt-24px px-40px">
                <table class="rounded-corners w-full">
                  <thead>
                    <tr>
                      <th>{{ $t('cooperation.partner.tableHead2') }}</th>
                      <th width="25%">{{ $t('cooperation.partner.tableHead3') }}</th>
                      <th width="18%">{{ $t('cooperation.partner.tableHead4') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-if="contributorListing.length">
                      <tr v-for="item in contributorListing" :key="item.donorId">
                        <td>{{ item.donorName }}</td>
                        <td>{{ item.donateTarget }}</td>
                        <td>{{ item.donateLevel }}</td>
                      </tr>
                    </template>
                    <template v-else>
                      <tr>
                        <td colspan="3">{{ $t('cooperation.partner.tableEmpty') }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>

              <!-- <p class="m-0 mt-8px px-40px text-14px color-hex-9A9EA6">{{ $t('cooperation.partner.contributorNotice') }}</p> -->

              <div class="pagination flex justify-center" v-if="contributorListing.length">
                <div class="pagination__container flex items-center">
                  <div class="previous">
                    <a herf="javascript:;" class="block cursor-pointer" :class="{ invisible: currentPage < 2 }" @click="previous">
                      <span class="i-home-icons:arrow-left block size-16"></span>
                    </a>
                  </div>
                  <div class="input box-border">
                    <input type="text" class="m-0 block h-full w-full border-0 bg-transparent p-0 text-center outline-none" v-model="current" @keydown.enter="submit" @blur="blur">
                  </div>
                  <div class="separator">/</div>
                  <div class="total">{{ totalPage }}</div>
                  <div class="next">
                    <a herf="javascript:;" class="block cursor-pointer" :class="{ invisible: currentPage >= totalPage }" @click="next">
                      <span class="i-home-icons:arrow-right block size-16"></span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.page-cooperation-partner {
  .cooperation-partner-container {
    @apply w-1360px <2xl:w-1200px;

    h3 {
      font-size: 32px;
    }

    .cooperation-partner-content {
      min-height: 560px;
      margin-top: 32px;
      background-image: url(@@home/assets/cooperation/partner/bg.svg);

      .partner-content-box {
        @apply w-1200px pt-48px <2xl:w-1040px;

        .partner-content-bd__toolbar {
          .filters {
            color: #fff;

            label {
              cursor: pointer;
            }

            .category-main {
              /**remove default style**/
              [type="radio"]:not(:checked),
              [type="radio"]:checked {
                position: absolute;
                left: -9999px;
                visibility: hidden;
              }

              [type="radio"] + .label-text {
                position: relative;
                display: block;
                min-width: 32px;
                font-size: 16px;
                line-height: 36px;
                white-space: nowrap;
                text-align: center;
                transition: .05s ease;
              }

              [type="radio"] + .label-text:after {
                position: absolute;
                bottom: -8px;
                left: 50%;
                z-index: 0;
                width: 32px;
                height: 2px;
                margin-left: -16px;
                background: #fff;
                border-radius: 1px;
                transition: .28s ease;
                content: "";
              }

              /* Unchecked styles */
              [type="radio"]:not(:checked) + .label-text:after {
                opacity: 0;
              }

              /* Checked styles */
              [type="radio"]:checked + .label-text {
                font-weight: 500;
                font-size: 18px;
              }

              [type="radio"]:checked + .label-text:after {
                opacity: 1;
              }
            }
          }

          .search {
            &.active {
              .input-group {
                background-color: #fbfbfb;
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);

                input {
                  color: #2d2d2d;

                  &::placeholder {
                    color: #9a9ea6;
                  }
                }

                .icon {
                  span {
                    color: #1180ff;
                  }
                }
              }
            }

            &:hover:not(.active) {
              .input-group {
                background-color: rgba(255, 255, 255, 0.5);
              }
            }

            .input-group {
              z-index: 1;
              // width: 320px;
              height: 48px;
              background-color: rgba(255, 255, 255, 0.2);
              border-radius: 4px;

              .icon {
                width: 48px;

                span {
                  width: 16px;
                  height: 16px;
                  color: #fff;
                }
              }

              input {
                color: #fff;
                font-size: 16px;
                background-color: transparent;

                &::placeholder {
                  color: #fff;
                }
              }
            }

            .popover {
              top: 100%;
              z-index: 0;
              margin-top: -4px;
              background: #fff;
              border-radius: 0 0 4px 4px;

              dl {
                padding-top: 12px;
                padding-right: 16px;
                padding-bottom: 24px;
                padding-left: 16px;

                dt {
                  margin-top: 8px;
                  color: #9a9ea6;
                  font-size: 12px;
                }

                dd {
                  margin-top: 8px;

                  li {
                    padding: 8px;
                    color: #2d2d2d;
                    font-size: 12px;
                    background-color: #fbfbfb;
                    border-radius: 2px;
                    @apply cursor-pointer;

                    &:hover {
                      color: #1180ff;
                      background-color: rgba(17, 128, 255, 0.05);
                    }

                    + li {
                      margin-left: 24px;
                    }
                  }
                }
              }
            }
          }
        }

        .partner-content-bd__table {
          margin-top: 40px;
          padding: 48px 40px;
          background-color: #fff;
          border-radius: 4px;
          box-shadow: 0 0 60px 0 rgba(17, 128, 255, 0.1);

          .rounded-corners {
            --border: 1px solid #1180ff;
            overflow: hidden;
            border: var(--border);
            border-radius: 4px;
            border-collapse: separate;
            border-spacing: 0;

            th:not(:last-child),
            td:not(:last-child) {
              border-right: var(--border);
            }

            > thead > tr:not(:last-child) > th,
            > thead > tr:not(:last-child) > td,
            > tbody > tr:not(:last-child) > th,
            > tbody > tr:not(:last-child) > td,
            > tfoot > tr:not(:last-child) > th,
            > tfoot > tr:not(:last-child) > td,
            > tr:not(:last-child) > td,
            > tr:not(:last-child) > th,
            > thead:not(:last-child),
            > tbody:not(:last-child),
            > tfoot:not(:last-child) {
              border-bottom: var(--border);
            }
          }

          table {
            th {
              height: 56px;
              color: #fff;
              font-size: 24px;
              line-height: 22px;
              background-color: #1180ff;

              @apply font-medium;
            }

            td {
              height: 48px;
              font-size: 16px;

              @apply text-center;
            }
          }

          .pagination {
            margin-top: 24px;
            font-size: 14px;

            .previous {
              &:hover {
                color: #1180ff;
              }
            }

            .input {
              width: 32px;
              height: 24px;
              margin-left: 10px;
              padding-right: 6px;
              padding-left: 6px;
              border: 1px solid #9a9ea6;
              border-radius: 4px;

              &:hover,
              &:focus-within {
                border: 1px solid #1180ff;
              }
            }

            .separator {
              margin-left: 13px;
            }

            .total {
              margin-left: 10px;
            }

            .next {
              margin-left: 10px;

              &:hover {
                color: #1180ff;
              }
            }
          }
        }
      }
    }

    .contributors {
      h4 {
        font-size: 24px;

        span {
          padding: 0 32px;
        }

        &::before {
          height: 2px;
          background: linear-gradient(116.01deg, rgba(91, 230, 255, 0) 0%, rgba(17, 128, 255, 0.5) 100%);
          opacity: 0.2;
        }

        &::after {
          height: 2px;
          background: linear-gradient(243.99deg, rgba(91, 230, 255, 0) 0%, rgba(17, 128, 255, 0.5) 100%);
          opacity: 0.2;
        }
      }

      ul {
        margin-top: 24px;

        li {
          width: 190px;
          height: 70px;
          background-color: #fff;
          // border-radius: 4px;

          @apply overflow-hidden;

          &:hover {
            img {
              width: 110%;
              height: 110%;
              margin-top: -2%;
              margin-left: -5%;
            }
          }

          img {
            display: block;
            width: 100%;
            height: 100%;
            transition: all 0.3s;

            @apply object-contain;
          }
        }
      }
    }
  }

  .custom-scrollbar {
    mask-image: linear-gradient(to left, transparent, black), linear-gradient(to top, transparent 4px, black 4px);
    mask-size: 20000px 100%;
    mask-position: right top;
    transition: mask-position 0.3s;

    &::-webkit-scrollbar {
      height: 4px;
      background-color: rgba(83, 85, 89, 0.08);
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(83, 85, 89, 0.15);
    }

    &:hover {
      mask-position: left top;
    }
  }
}
</style>
